import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconAlertTriangle} from '@tabler/icons-react';
import React from 'react';
import type {InspectionScopeDeviceResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Pill} from 'src/design/atoms/Pill';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import type {VirtualizedTableColumn} from 'src/design/organisms/VirtualizedTable';
import {VirtualizedTable} from 'src/design/organisms/VirtualizedTable';
import {formatDeviceLocation} from 'src/app/inspections/deviceRollup';
import {ComplianceStateEnumPill} from 'src/shared/constants/PillConstants';

export interface InspectionDeviceTableProps {
  devices: InspectionScopeDeviceResponse[];
  /** False shows "View" instead of "Record". */
  canRecord: boolean;
  onEnterDevice: (device: InspectionScopeDeviceResponse) => void;
}

const DeviceCell: React.FC<{device: InspectionScopeDeviceResponse}> = ({
  device,
}) => {
  const name = device.name ?? `Device #${device.deviceId}`;
  return (
  <Box style={{minWidth: 0}}>
    <Group gap={4} wrap="nowrap">
      {/* Native title tooltip — cheap for virtualized rows; no per-row Mantine Tooltip. */}
      <Text size="sm" fw={500} truncate title={name}>
        {name}
      </Text>
      {!device.isVerified && (
        <Tooltip label="Added in the field — pending verification">
          <ThemeIcon size="sm" variant="transparent" color="yellow">
            <IconAlertTriangle size={14} />
          </ThemeIcon>
        </Tooltip>
      )}
      {device.deviceIsDeleted && (
        <Text span size="xs" c="dimmed">
          (deleted)
        </Text>
      )}
    </Group>
  </Box>
  );
};

const ActionCell: React.FC<{
  device: InspectionScopeDeviceResponse;
  canRecord: boolean;
  onEnterDevice: (device: InspectionScopeDeviceResponse) => void;
}> = ({device, canRecord, onEnterDevice}) => {
  const isUnentered = device.complianceState == null;
  // A not-yet-started (Draft) inspection has nothing to record or view yet.
  const canOpen = canRecord || !isUnentered;

  return (
    <Button
      size="compact-sm"
      variant={canRecord && isUnentered ? 'filled' : 'subtle'}
      onClick={() => onEnterDevice(device)}
      disabled={!canOpen}>
      {canRecord && isUnentered ? 'Record' : 'View'}
    </Button>
  );
};

// Flat, virtualized device list for the record screen (LocationTree owns the
// Building/Floor/Room grouping). Rows are display-filtered only, never excluded here.
export const InspectionDeviceTable: React.FC<InspectionDeviceTableProps> = ({
  devices,
  canRecord,
  onEnterDevice,
}) => {
  const columns: VirtualizedTableColumn<InspectionScopeDeviceResponse>[] = [
    {
      id: 'device',
      header: 'Device',
      minWidth: 180,
      render: device => <DeviceCell device={device} />,
    },
    {
      id: 'type',
      header: 'Type',
      width: 140,
      render: device => <Text size="sm" truncate>{device.equipmentTypeName ?? '—'}</Text>,
    },
    {
      id: 'serial',
      header: 'Serial',
      width: 120,
      render: device => <Text size="sm" truncate>{device.serialNumber ?? '—'}</Text>,
    },
    {
      id: 'location',
      header: 'Location',
      width: 180,
      render: device => <Text size="sm" truncate>{formatDeviceLocation(device)}</Text>,
    },
    {
      id: 'state',
      header: 'State',
      width: 130,
      render: device => <Pill {...ComplianceStateEnumPill(device.complianceState)} />,
    },
    {
      id: 'action',
      header: '',
      width: 100,
      align: 'right',
      render: device => (
        <ActionCell
          device={device}
          canRecord={canRecord}
          onEnterDevice={onEnterDevice}
        />
      ),
    },
  ];

  return (
    <VirtualizedTable
      columns={columns}
      items={devices}
      rowKey={device => device.deviceId}
      stickyHeader
      height="60vh"
      // Sum of the fixed/min column widths below — keeps the action column reachable
      // via horizontal scroll instead of clipping off-canvas at narrower content widths.
      minWidth={850}
      emptyState={
        <NoItemsPanel noItemsText="No devices match the current filters." />
      }
    />
  );
};
